import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function useProfile() {
  return useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) throw new Error("not signed in");
      const { data, error } = await supabase.from("profiles").select("*").eq("id", u.user.id).maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function usePlans() {
  return useQuery({
    queryKey: ["plans"],
    queryFn: async () => {
      const { data, error } = await supabase.from("plans").select("*").eq("is_active", true).order("sort_order");
      if (error) throw error;
      return data;
    },
  });
}

export function useInvestments() {
  return useQuery({
    queryKey: ["investments"],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return [];
      const { data, error } = await supabase
        .from("investments")
        .select("*, plan:plans(*)")
        .eq("user_id", u.user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

export function useTransactions(limit?: number) {
  return useQuery({
    queryKey: ["transactions", limit],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return [];
      let q = supabase.from("transactions").select("*").eq("user_id", u.user.id).order("created_at", { ascending: false });
      if (limit) q = q.limit(limit);
      const { data, error } = await q;
      if (error) throw error;
      return data;
    },
  });
}

export function useDeposits() {
  return useQuery({
    queryKey: ["deposits"],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return [];
      const { data, error } = await supabase.from("deposits").select("*, plan:plans(name)").eq("user_id", u.user.id).order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

export function useWithdrawals() {
  return useQuery({
    queryKey: ["withdrawals"],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return [];
      const { data, error } = await supabase.from("withdrawals").select("*").eq("user_id", u.user.id).order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

export function useCreateDeposit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { amount: number; plan_id?: string | null; btc_address?: string; asset?: "BTC" | "ETH" }) => {
      const { data, error } = await supabase.rpc("create_deposit_request", {
        _amount: input.amount,
        _plan_id: (input.plan_id ?? null) as unknown as string,
        _btc_address: (input.btc_address ?? null) as unknown as string,
        _asset: input.asset ?? "BTC",
      });
      if (error) throw error;
      return { id: data as string };
    },
    onSuccess: () => {
      toast.success("Deposit request submitted — awaiting approval");
      qc.invalidateQueries({ queryKey: ["deposits"] });
      qc.invalidateQueries({ queryKey: ["transactions"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useCreateWithdrawal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { amount: number; btc_address: string }) => {
      const { data, error } = await supabase.rpc("create_withdrawal_request", {
        _amount: input.amount,
        _btc_address: input.btc_address,
      });
      if (error) throw error;
      return { id: data as string };
    },
    onSuccess: () => {
      toast.success("Withdrawal requested");
      qc.invalidateQueries({ queryKey: ["withdrawals"] });
      qc.invalidateQueries({ queryKey: ["transactions"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useReferrals() {
  return useQuery({
    queryKey: ["referrals"],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return [];
      const { data: refs, error } = await supabase
        .from("referrals")
        .select("*")
        .eq("referrer_id", u.user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      if (!refs?.length) return [];
      const ids = refs.map((r) => r.referee_id);
      const { data: profs } = await supabase.from("profiles").select("id, email, full_name, total_deposited").in("id", ids);
      const byId = new Map((profs ?? []).map((p) => [p.id, p]));
      return refs.map((r) => ({ ...r, referee: byId.get(r.referee_id) ?? null }));
    },
  });
}

export function useAnnouncements() {
  return useQuery({
    queryKey: ["announcements"],
    queryFn: async () => {
      const { data, error } = await supabase.from("announcements").select("*").eq("is_active", true).order("created_at", { ascending: false }).limit(5);
      if (error) throw error;
      return data;
    },
  });
}

export function useIsAdmin() {
  return useQuery({
    queryKey: ["is-admin"],
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return false;
      const { data, error } = await supabase.rpc("has_role", { _user_id: u.user.id, _role: "admin" });
      if (error) throw error;
      return !!data;
    },
  });
}

export function useBotTiers() {
  return useQuery({
    queryKey: ["bot-tiers"],
    queryFn: async () => {
      const { data, error } = await supabase.from("bot_tiers").select("*").eq("is_active", true).order("sort_order");
      if (error) throw error;
      return data;
    },
  });
}

export function useUserBots() {
  return useQuery({
    queryKey: ["user-bots"],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return [];
      const { data, error } = await supabase
        .from("user_bots")
        .select("*, tier:bot_tiers(*)")
        .eq("user_id", u.user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

export function useActivateBot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { tier_id: string; amount: number }) => {
      const { data, error } = await supabase.rpc("activate_bot", { _tier_id: input.tier_id, _amount: input.amount });
      if (error) throw error;
      return { id: data as string };
    },
    onSuccess: () => {
      toast.success("Bot activated — trading has begun");
      qc.invalidateQueries({ queryKey: ["user-bots"] });
      qc.invalidateQueries({ queryKey: ["profile"] });
      qc.invalidateQueries({ queryKey: ["transactions"] });
      qc.invalidateQueries({ queryKey: ["locked-balance"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useClaimBotProfits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (bot_id: string) => {
      const { data, error } = await supabase.rpc("claim_bot_profits", { _bot_id: bot_id });
      if (error) throw error;
      return Number(data ?? 0);
    },
    onSuccess: (amt) => {
      if (amt > 0) toast.success(`Claimed $${amt.toFixed(2)} in profits`);
      else toast.info("No profits to claim yet");
      qc.invalidateQueries({ queryKey: ["user-bots"] });
      qc.invalidateQueries({ queryKey: ["profile"] });
      qc.invalidateQueries({ queryKey: ["transactions"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useLockedBalance() {
  return useQuery({
    queryKey: ["locked-balance"],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return 0;
      const { data, error } = await supabase.rpc("get_locked_balance", { _uid: u.user.id });
      if (error) throw error;
      return Number(data ?? 0);
    },
  });
}